import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { CATEGORIES, POSTS, Post } from "@/mocks/gossip";

type Filter = "全部" | string;

export default function FeedArea() {
  const [active, setActive] = useState<Filter>("全部");
  const [visible, setVisible] = useState(4);

  const names = ["全部", ...CATEGORIES.map((c) => c.name)];

  const list = useMemo<Post[]>(() => {
    const filtered =
      active === "全部" ? POSTS : POSTS.filter((p) => p.category === active);
    return filtered;
  }, [active]);

  const shown = list.slice(0, visible);
  const hasMore = visible < list.length;

  const hotRank = [...POSTS].sort((a, b) => b.hot - a.hot).slice(0, 5);

  const rankMedals = ["text-accent-500", "text-foreground-400", "text-primary-400"];

  return (
    <section id="feed" className="scroll-mt-24 mx-auto max-w-7xl px-4 md:px-6 py-14 md:py-20">
      {/* 板块标题 */}
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
        <div>
          <span className="inline-flex items-center gap-1.5 text-xs font-medium text-primary-600 bg-primary-100 rounded-full px-3 py-1 mb-3">
            <i className="ri-flashlight-line"></i> 新瓜速递
          </span>
          <h2 className="font-heading text-2xl md:text-4xl text-foreground-950">
            今日新瓜 · 一手速递
          </h2>
        </div>
        <div className="flex flex-wrap gap-2">
          {names.map((n) => (
            <button
              key={n}
              onClick={() => {
                setActive(n);
                setVisible(4);
              }}
              className={`px-4 py-1.5 rounded-full text-sm whitespace-nowrap transition ${
                active === n
                  ? "bg-primary-500 text-background-50"
                  : "bg-background-100 text-foreground-600 hover:bg-background-200"
              }`}
            >
              {n}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* 左侧内容列表 */}
        <div className="lg:col-span-8 space-y-5">
          {shown.map((p) => (
            <article
              key={p.id}
              className="group grid grid-cols-1 sm:grid-cols-12 gap-4 rounded-3xl border border-background-200 bg-background-50 p-3 hover:border-primary-200 hover:shadow-sm transition"
            >
              <Link
                to={`/post/${p.slug}`}
                className="sm:col-span-4 block rounded-2xl overflow-hidden h-40 sm:h-auto"
              >
                <img
                  src={p.cover}
                  alt={`${p.title} - 九一吃瓜新瓜速递`}
                  title={p.title}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
              </Link>
              <div className="sm:col-span-8 flex flex-col justify-center p-1 sm:pr-2">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs font-medium text-primary-600 bg-primary-50 rounded-md px-2 py-0.5">
                    {p.category}
                  </span>
                  <span className="text-xs text-foreground-400">{p.tag}</span>
                </div>
                <h3 className="text-base md:text-xl font-bold text-foreground-950 leading-snug group-hover:text-primary-600 transition">
                  <Link to={`/post/${p.slug}`}>{p.title}</Link>
                </h3>
                <p className="mt-2 text-sm text-foreground-500 leading-relaxed line-clamp-2 hidden md:block">
                  {p.summary}
                </p>
                <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-foreground-400">
                  <span className="flex items-center gap-1">
                    <i className="ri-time-line"></i>
                    {p.date}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-eye-line"></i>
                    {p.views}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-fire-line text-primary-500"></i>
                    热{p.hot}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-book-open-line"></i>
                    {p.minutes}分钟
                  </span>
                </div>
              </div>
            </article>
          ))}

          {hasMore && (
            <div className="pt-2 text-center">
              <button
                onClick={() => setVisible((v) => v + 4)}
                className="inline-flex items-center gap-2 rounded-full border border-primary-200 bg-background-50 text-primary-600 text-sm font-medium px-6 py-2.5 hover:bg-primary-500 hover:text-background-50 transition whitespace-nowrap"
              >
                <i className="ri-arrow-down-line"></i> 查看更多新瓜
              </button>
            </div>
          )}
        </div>

        {/* 右侧热度榜 */}
        <aside className="lg:col-span-4">
          <div id="topic" className="scroll-mt-24 rounded-3xl border border-accent-200 bg-gradient-to-br from-accent-50 to-background-50 p-5 md:p-6 sticky top-24">
            <div className="flex items-center justify-between mb-5">
              <h2 className="flex items-center gap-2 text-lg font-bold text-foreground-950">
                <i className="ri-fire-fill text-primary-500"></i> 今日热度榜
              </h2>
              <span className="text-xs text-foreground-400">实时更新</span>
            </div>

            <ol className="space-y-1">
              {hotRank.map((p, i) => (
                <li key={p.id}>
                  <Link
                    to={`/post/${p.slug}`}
                    className="flex items-start gap-3 rounded-xl p-2 hover:bg-background-50/80 transition"
                  >
                    <span
                      className={`w-8 h-8 shrink-0 flex items-center justify-center rounded-lg font-heading text-lg ${
                        i < 3 ? rankMedals[i] : "text-foreground-300"
                      }`}
                    >
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <div className="min-w-0">
                      <h3 className="text-sm font-medium text-foreground-800 leading-snug line-clamp-2 group-hover:text-primary-600">
                        {p.title}
                      </h3>
                      <span className="text-xs text-foreground-400">{p.category} · {p.views}</span>
                    </div>
                  </Link>
                  {i < hotRank.length - 1 && <div className="mx-2 h-px bg-accent-200/70"></div>}
                </li>
              ))}
            </ol>

            <a
              href="#feed"
              onClick={(e) => {
                e.preventDefault();
                document.getElementById("feed")?.scrollIntoView({ behavior: "smooth" });
              }}
              className="mt-4 block text-center rounded-full bg-accent-500 text-background-50 text-sm font-medium py-2.5 hover:bg-accent-600 transition whitespace-nowrap"
            >
              查看更多前排现场
            </a>
          </div>
        </aside>
      </div>
    </section>
  );
}